// Shared helper: render an HTML body+CSS string into a real downloadable PDF
// using html2pdf.js.
//
// Why it's not just "html2pdf.from(node).save()":
//   html2canvas (which html2pdf wraps) clones the target DOM into a temporary
//   iframe. Stylesheets are copied, but inline <style> tags WITHIN the cloned
//   subtree are not always re-applied (depends on the browser). The reliable
//   pattern is therefore:
//     1. Inject the CSS into document.head (it'll be copied to the clone)
//     2. Render the container *visibly* on-screen (opacity:0 or off-screen
//        positioning routinely produces a blank canvas in some browsers)
//     3. Cover it with a temporary white overlay so the user doesn't see
//        the raw markup flash during the 2-5 second rasterisation.
//     4. Strip the helper styles when we're done.

import html2pdf from "html2pdf.js";

const ROOT_CLASS = "hireloop-pdf-export";
const STYLE_ATTR = "data-hireloop-pdf-export";

function ensureStyles(rawStyles) {
  // Re-scope ".pdf-root" selectors used in the existing PDF templates to our
  // namespaced root class, then inject into <head> so html2canvas sees the
  // rules as part of the document stylesheet during DOM clone.
  const scoped = rawStyles.replace(/\.pdf-root/g, `.${ROOT_CLASS}`);
  const styleEl = document.createElement("style");
  styleEl.setAttribute(STYLE_ATTR, "1");
  styleEl.textContent = `.${ROOT_CLASS}, .${ROOT_CLASS} * { box-sizing: border-box; }\n${scoped}`;
  document.head.appendChild(styleEl);
  return styleEl;
}

function createOverlay(label) {
  const overlay = document.createElement("div");
  overlay.setAttribute("data-pdf-export-overlay", "1");
  Object.assign(overlay.style, {
    position: "fixed",
    inset: "0",
    background: "#ffffff",
    zIndex: "2147483647",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    font: '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif',
    color: "#1d4ed8",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
  });
  overlay.innerHTML = `
    <div style="width: 42px; height: 42px; border-radius: 50%; border: 3px solid #dbeafe; border-top-color: #1d4ed8; animation: hireloop-pdf-spin 0.85s linear infinite;"></div>
    <div>${label}</div>
    <style>@keyframes hireloop-pdf-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }</style>
  `;
  return overlay;
}

/**
 * Generate a PDF from an HTML fragment.
 *
 * @param {Object}  opts
 * @param {string}  opts.filename   Output filename (e.g. "Hireloop-Brochure.pdf")
 * @param {string}  opts.styles     CSS rules (without <style> tags); uses ".pdf-root" prefix
 * @param {string}  opts.body       HTML markup to render
 * @param {string} [opts.width]     Container width (default "8.5in")
 * @param {string} [opts.overlayLabel]  Loading-overlay label (default "Generating PDF…")
 * @param {Object} [opts.pdfOptions] Extra html2pdf options to merge
 */
export async function generatePdfFromHtml({
  filename,
  styles,
  body,
  width = "8.5in",
  overlayLabel = "Generating PDF…",
  pdfOptions = {},
}) {
  const styleEl = ensureStyles(styles);
  const overlay = createOverlay(overlayLabel);
  const container = document.createElement("div");
  container.className = ROOT_CLASS;
  container.setAttribute("data-pdf-export", "1");
  // Render visibly at top-left of the viewport. The overlay (zIndex max) sits
  // ON TOP so the user only sees the spinner.
  Object.assign(container.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width,
    background: "#ffffff",
    zIndex: "2147483646",
    overflow: "visible",
  });
  container.innerHTML = body;

  document.body.appendChild(container);
  document.body.appendChild(overlay);

  // Allow images (logo, etc.) to load before rasterising
  const images = container.querySelectorAll("img");
  await Promise.all(
    Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) return resolve();
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        })
    )
  );

  // Give the browser two frames to commit layout before html2canvas measures.
  await new Promise((r) => requestAnimationFrame(() => r()));
  await new Promise((r) => requestAnimationFrame(() => r()));

  const rect = container.getBoundingClientRect();
  const widthPx = rect.width || 816;

  const options = {
    margin: 0,
    filename,
    image: { type: "jpeg", quality: 0.95 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
      windowWidth: widthPx,
      width: widthPx,
      height: rect.height || undefined,
      scrollX: 0,
      scrollY: 0,
    },
    jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    pagebreak: { mode: ["css", "legacy"] },
    ...pdfOptions,
  };

  try {
    await html2pdf().set(options).from(container).save();
  } finally {
    container.remove();
    overlay.remove();
    styleEl.remove();
  }
}


/**
 * Generate a multi-page PDF by rasterising each section into its own canvas
 * and composing the canvases with jsPDF.addPage(). Avoids html2pdf's flaky
 * page-break splitter — each section gets EXACTLY one fresh page.
 *
 * @param {Object}   opts
 * @param {string}   opts.filename
 * @param {string}   opts.styles            CSS rules (".pdf-root" prefix supported)
 * @param {string[]} opts.sections          Array of HTML fragments — one per page
 * @param {string}   [opts.overlayLabel]    Loader label
 * @param {string}   [opts.width]           Container width (default "8.5in")
 */
export async function generateMultiPagePdfFromHtml({
  filename,
  styles,
  sections,
  overlayLabel = "Generating PDF…",
  width = "8.5in",
}) {
  if (!Array.isArray(sections) || sections.length === 0) {
    throw new Error("generateMultiPagePdfFromHtml: sections must be a non-empty array");
  }

  const html2canvas = (await import("html2canvas")).default;
  const { jsPDF } = await import("jspdf");

  const styleEl = ensureStyles(styles);
  const overlay = createOverlay(overlayLabel);
  document.body.appendChild(overlay);

  // Letter page in points (72 dpi): 8.5 * 72 = 612, 11 * 72 = 792
  const PAGE_W_PT = 612;
  const PAGE_H_PT = 792;
  const pdf = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });

  try {
    for (let i = 0; i < sections.length; i++) {
      const container = document.createElement("div");
      container.className = ROOT_CLASS;
      container.setAttribute("data-pdf-export", "1");
      Object.assign(container.style, {
        position: "fixed",
        top: "0",
        left: "0",
        width,
        background: "#ffffff",
        zIndex: "2147483646",
        overflow: "visible",
      });
      container.innerHTML = sections[i];
      document.body.appendChild(container);

      // Wait for images to load
      const images = container.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) return resolve();
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            })
        )
      );
      await new Promise((r) => requestAnimationFrame(() => r()));
      await new Promise((r) => requestAnimationFrame(() => r()));

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const ratio = canvas.height / canvas.width;
      const imgWidthPt = PAGE_W_PT;
      const imgHeightPt = imgWidthPt * ratio;

      // Add fresh page for everything except the first section.
      if (i > 0) pdf.addPage();

      if (imgHeightPt <= PAGE_H_PT) {
        // Whole section fits on one page.
        pdf.addImage(imgData, "JPEG", 0, 0, imgWidthPt, imgHeightPt);
      } else {
        // Section taller than a page → slice it across as many pages as needed.
        let renderedHeightPt = 0;
        let pageIndex = 0;
        while (renderedHeightPt < imgHeightPt) {
          if (pageIndex > 0) pdf.addPage();
          pdf.addImage(
            imgData,
            "JPEG",
            0,
            -renderedHeightPt,
            imgWidthPt,
            imgHeightPt
          );
          renderedHeightPt += PAGE_H_PT;
          pageIndex += 1;
        }
      }

      container.remove();
    }

    pdf.save(filename);
  } finally {
    overlay.remove();
    styleEl.remove();
  }
}
